/**
 * @prettier
 */

import React, { Component } from "react";
import { Route, Switch } from "react-router-dom";
import { ConnectedRouter } from "connected-react-router";
import { PersistGate } from "redux-persist/es/integration/react";
import { connect, Provider } from "react-redux";
import { bindActionCreators } from "redux";
import i18next from "i18next";
import { withTranslation } from "react-i18next";
// Actions
import { setInitializationParams } from "./state/actions/configActions.js";
import {
    isHandoff,
    setIsMobileHandoffSubmission,
} from "./state/actions/submissionStateActions.js";
import {
    awaitResponse,
    responseReceived,
} from "./state/actions/spinnerActions.js";

// Services
import {
    areCookiesEnabled,
    isActiveMobileHandoffSession,
    updateUrlLanguageParam,
} from "./services/shared/helpers.js";
import selectStorage from "./services/shared/selectStorage.js";

// Components
import AppEntryPage from "./pages/AppEntryPage";
import StartPage from "./pages/StartPage";
import OcrVerificationPage from "./pages/OcrVerificationPage/index.jsx";
import AcuantResultPage from "./pages/ProcessedImageResultPage/Acuant";
import RegulaResultPage from "./pages/ProcessedImageResultPage/Regula";
import CaptureDocumentPage from "./pages/CaptureDocumentPage";
import RegulaCapturePage from "./pages/RegulaCapturePage";
import InstructionsPage from "./pages/InstructionsPage";
import CaptureSelfiePage from "./pages/CaptureSelfiePage";
import ConsentPage from "./pages/ConsentPage";
import ProofOfAddressPage from "./pages/ProofOfAddressPage";
import MobileHandoffPage from "./pages/MobileHandoffPage";
import AcuantCameraPage from "./pages/AcuantCameraPage";
import SuccessPage from "./pages/SuccessPage";
import CaptureSelfieFallback from "./pages/CaptureSelfiePage/CaptureSelfieFallback.jsx";
import Error from "./pages/ErrorPage";
import ErrorBoundary from "./pages/ErrorPage/ErrorBoundary";
import CapturePhotoPage from "./pages/CapturePhotoPage/index.jsx";
import GroupLinkStartPage from "./pages/GroupLinkStartPage";
import GroupLinkSendPage from "./pages/GroupLinkSendPage";

// Styles
import "./styles/main.scss";

// Config
import { ACTION_LABELS } from "./config/dataDogActionLabels";
import SelectDocumentPage from "./pages/SelectDocumentPage/";
import EVerificationPage from "./pages/EverificationPage/";
import ReusableProfileStartPage from "./pages/ReusableProfileStartPage";

const ACTION = ACTION_LABELS.app;

/*
global Raven
 */

function storeUserId(queryParams) {
    /*
        Replaced the logic localstorage with the selectStorage(areCookiesEnabled()), because the localstorage values are changed 
        in all tabs for the browser, the selectStorage(areCookiesEnabled()) value changed only the current tab of the browser
    */
    const storage = selectStorage(areCookiesEnabled());
    storage.removeItem("sessionid"); // always generate a new session onload
    storage.removeItem("submissionid"); // always get new submission onload
    storage.removeItem("target");
    decodeURI(queryParams)
        .replace("?", "")
        .split("&")
        .map(param => param.split("="))
        .reduce((values, [key, value]) => {
            if (key !== undefined || value !== undefined) {
                storage.removeItem(key);
                storage.setItem(key, value);
            }
            return null;
        }, {});
    if (storage.getItem("target") !== "webapp_mobile") {
        storage.removeItem("handoff_session");
    }
}

function setHandoffStateFromStorage({
    setIsMobileHandoffSubmission,
    isHandoff,
}) {
    const storage = selectStorage(areCookiesEnabled());
    const isActiveHandoff = isActiveMobileHandoffSession(storage);

    setIsMobileHandoffSubmission(isActiveHandoff);
    isHandoff(isActiveHandoff);
}

class App extends Component {
    constructor(props) {
        // checks that essential cookies are enabled before storing url paramaters
        // parse the data from the iframe query string and save to local storage for all
        // api requests. please note that when on the success page you click finish and the session is
        // deleted and then regenerated again as the app is forced to reload
        // device_type header also set here
        // /Applications/MAMP/htdocs/Apps/id-pal-kyc-app/frontend/src/state/actions/configActions.js

        super(props);
        this.state = {
            isAcuantSdkLoaded: false,
        };
        this.isInitialized = false;
        this.isIntializing = false;
    }

    componentDidMount() {
        storeUserId(window.location.search);
        setHandoffStateFromStorage(this.props);

        this.setLanguage();

        if (
            process.env.REACT_APP_SENTRY_SUBSCRIPTION_ID &&
            process.env.REACT_APP_SENTRY_SUBSCRIPTION_ID.length > 0
        ) {
            Raven.config(
                process.env.REACT_APP_SENTRY_SUBSCRIPTION_ID
            ).install();
        }

        if (!this.props.config) {
            this.props.routerHistory.replace("/");
        }
    }

    componentDidCatch(error, errorInfo) {
        if (
            process.env.REACT_APP_SENTRY_SUBSCRIPTION_ID &&
            process.env.REACT_APP_SENTRY_SUBSCRIPTION_ID.length > 0
        ) {
            Raven.captureException(error, { extra: errorInfo });
        }
        this.props.routerHistory.push("/error/default");
    }

    getBrandTheme() {
        let brandTheme = "id-pal";
        brandTheme =
            selectStorage(areCookiesEnabled()).getItem("brand") !== null &&
            selectStorage(areCookiesEnabled()).getItem("brand");
        return brandTheme;
    }

    setLanguage() {
        // Switch to US English if in US Region.
        const initialLang = selectStorage(areCookiesEnabled()).getItem("lang");
        if (
            process.env.REACT_APP_US_ENGLISH === "true" &&
            initialLang === "en"
        ) {
            updateUrlLanguageParam("en-US");
        }

        this.props.setInitializationParams(window.location.search);

        // Checks that essential cookies are enabled before setting the new language
        const lang = selectStorage(areCookiesEnabled()).getItem("lang");
        lang ? i18next.changeLanguage(lang) : i18next.changeLanguage("en");
    }

    render() {
        const { t } = this.props;

        return (
            <div
                className={"mainContent brand-" + this.getBrandTheme()}
                data-dd-action-name={ACTION.name}
            >
                {
                    <Provider store={this.props.store}>
                        <PersistGate
                            loading={null}
                            persistor={this.props.persistor}
                        >
                            <ConnectedRouter history={this.props.routerHistory}>
                                <ErrorBoundary>
                                    <Switch>
                                        <Route
                                            path="/"
                                            exact
                                            component={AppEntryPage}
                                        />
                                        <Route
                                            path="/start"
                                            exact
                                            component={StartPage}
                                        />
                                        <Route
                                            path="/re_verification"
                                            exact
                                            component={ReusableProfileStartPage}
                                        />
                                        <Route
                                            path="/ocr_verification"
                                            exact
                                            component={OcrVerificationPage}
                                        />
                                        <Route
                                            path="/select"
                                            exact
                                            component={SelectDocumentPage}
                                        />
                                        <Route
                                            path="/instruction/:type/:side"
                                            exact
                                            component={InstructionsPage}
                                        />
                                        <Route
                                            path="/capture/photo/:uuid?/:device_type?"
                                            exact
                                            component={CapturePhotoPage}
                                        />
                                        <Route
                                            path="/capture/camera/"
                                            exact
                                            component={
                                                this.props.captureMethod ===
                                                "alt"
                                                    ? RegulaCapturePage
                                                    : AcuantCameraPage
                                            }
                                        />
                                        <Route
                                            path="/capture/idpal_camera/"
                                            exact
                                            component={CaptureDocumentPage}
                                        />
                                        <Route
                                            path="/photo/confirm/:uuid?/:device_type?"
                                            exact
                                            component={
                                                this.props.captureMethod ===
                                                "alt"
                                                    ? RegulaResultPage
                                                    : AcuantResultPage
                                            }
                                        />
                                        <Route
                                            path="/capture/selfie/:uuid?/:device_type?"
                                            exact
                                            component={CaptureSelfiePage}
                                        />
                                        <Route
                                            path="/capture/idpal_selfie/:uuid?/:device_type?"
                                            exact
                                            component={CaptureSelfieFallback}
                                        />
                                        <Route
                                            path="/success/:uuid?/:device_type?"
                                            component={SuccessPage}
                                        />
                                        <Route
                                            path="/e_verification"
                                            exact
                                            component={EVerificationPage}
                                        />
                                        <Route
                                            path="/mobile_handoff/"
                                            exact
                                            component={MobileHandoffPage}
                                        />
                                        <Route
                                            path="/capture/proof_of_address/"
                                            exact
                                            component={ProofOfAddressPage}
                                        />
                                        <Route
                                            path="/error"
                                            component={Error}
                                        />
                                        <Route
                                            path="/consent"
                                            component={ConsentPage}
                                        />
                                        <Route
                                            path="/group_link"
                                            exact
                                            component={GroupLinkStartPage}
                                        />
                                        <Route
                                            path="/group_link_send"
                                            exact
                                            component={GroupLinkSendPage}
                                        />
                                    </Switch>
                                </ErrorBoundary>
                            </ConnectedRouter>
                        </PersistGate>
                    </Provider>
                }
                <div className="toast" id="reconnecting">
                    {t("idpal_reconnecting")}
                </div>
            </div>
        );
    }
}

function mapStateToProps(state) {
    return {
        instanceID: state.config.instanceID,
        captureMethod: state.submissionState.captureMethod,
    };
}

function mapDispatchToProps(dispatch) {
    return bindActionCreators(
        {
            awaitResponse,
            responseReceived,
            setInitializationParams,
            setIsMobileHandoffSubmission,
            isHandoff,
        },
        dispatch
    );
}

export default withTranslation("translation")(
    connect(mapStateToProps, mapDispatchToProps)(App)
);
